import React, { useEffect } from 'react'
import { PlaceVisitSummary } from '../api/review'
import { setAppStatusBarDark } from '../utils/flutterStatusBar'
import { useSheetDragDismiss } from '../hooks/useSheetDragDismiss'
import { formatScoreOneDecimal } from '../utils/scoreFormat'
import SheetHeader from './SheetHeader'
import PrivateBadge from './PrivateBadge'

interface VisitPickBottomSheetProps {
  isOpen: boolean
  visits: PlaceVisitSummary[]
  /** 지금 열려 있는 회차 */
  selectedId: number | null
  /** 점수를 가려야 하는 사용자인지 — 기록 화면과 같은 판정을 쓴다 */
  hideScore?: boolean
  onSelect: (visit: PlaceVisitSummary) => void
  onClose: () => void
}

/** 2026-05-14 → 2026년 5월 14일 */
function formatVisitDate(raw: string | null): string {
  if (!raw) return ''
  const m = raw.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (!m) return raw
  return `${m[1]}년 ${Number(m[2])}월 ${Number(m[3])}일`
}

/**
 * 어느 회차를 고칠지 고르는 시트.
 *
 * 기록이 2개 이상일 때만 연다. 하나뿐이면 고를 게 없어서 시트를 띄우는 것
 * 자체가 한 번 더 누르게 만드는 일이다.
 *
 * 최신 회차가 맨 위다. 카드의 스레드와 같은 순서라, 방금 본 화면과 여기가
 * 어긋나지 않는다.
 */
const VisitPickBottomSheet: React.FC<VisitPickBottomSheetProps> = ({
  isOpen,
  visits,
  selectedId,
  hideScore,
  onSelect,
  onClose,
}) => {
  useEffect(() => {
    if (!isOpen) return
    setAppStatusBarDark(true)
    return () => setAppStatusBarDark(false)
  }, [isOpen])

  const { handleProps, sheetStyle } = useSheetDragDismiss({
    onClose,
    enabled: isOpen,
  })

  if (!isOpen) return null

  return (
    <>
      <div
        className="fixed inset-0 bg-black bg-opacity-40 z-[1040]"
        onClick={onClose}
      />

      <div className="fixed bottom-0 left-0 right-0 z-[1050] flex justify-center">
        <div className="app-container flex flex-col">
          <div
            className="bg-ivoryKey rounded-t-30px shadow-lg overflow-hidden flex flex-col w-full"
            style={sheetStyle}
          >
            <div className="flex flex-col w-full" {...handleProps}>
              <div className="w-40px h-5px bg-d9d9d9 rounded-20px mx-auto mt-16px mb-16px"></div>
            </div>

            <SheetHeader title="어떤 기록을 수정할까요?" />

            <div className="px-20px pt-8px pb-32px flex flex-col max-h-[50vh] overflow-y-auto">
              {visits.map((v) => {
                const isSelected = v.id === selectedId
                return (
                  <button
                    key={v.id}
                    type="button"
                    onClick={() => onSelect(v)}
                    className={`w-full flex items-center py-16px text-left ${
                      isSelected ? '' : 'opacity-100'
                    }`}
                  >
                    <span className="flex-1 flex items-center min-w-0">
                      <span
                        className={`font-pretendard text-16px ${
                          isSelected
                            ? 'font-700 text-brown-80%-main'
                            : 'font-500 text-textColorText2'
                        }`}
                      >
                        {v.visit_number}번째 기록
                      </span>
                      {!v.is_public && (
                        <span className="ml-6px flex items-center">
                          <PrivateBadge />
                        </span>
                      )}
                      {v.visit_date && (
                        <span className="ml-8px text-style-captionM text-textColorText3 truncate">
                          {formatVisitDate(v.visit_date)}
                        </span>
                      )}
                    </span>
                    {hideScore ? (
                      <span className="ml-8px font-pretendard font-bold text-16px text-brown-80%-main flex-shrink-0">
                        ? 점
                      </span>
                    ) : (
                      v.score !== null && (
                        <span className="ml-8px font-pretendard font-bold text-16px text-brown-80%-main flex-shrink-0">
                          {formatScoreOneDecimal(v.score)} 점
                        </span>
                      )
                    )}
                  </button>
                )
              })}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default VisitPickBottomSheet
